import { Injectable } from '@angular/core';
import {
  catchError, map, Observable, shareReplay, throwError
} from 'rxjs';

import { ProviderNetworkCompanyHttpService } from '@sondermind/data-access/provider-network-company';
import { IInsurance, ProviderNetworkCompany } from '@sondermind/utilities/models-provider-network-company';
import { indexArray } from '@sondermind/utilities/tools';

@Injectable({ providedIn: 'root' })
export class ProviderNetworkCompanyStore {
  private cachedCompanies$?: Observable<ProviderNetworkCompany[]>;
  private cachedInsuranceOptionsByState = new Map<string, Observable<IInsurance[]>>();

  constructor(private http: ProviderNetworkCompanyHttpService) {}

  /**
   * Fetches the detail view for all ProviderNetworkCompany records.
   * @returns An observable that emits an array of all PNCs
   */
  getAll$(): Observable<ProviderNetworkCompany[]> {
    // eslint-disable-next-line no-return-assign
    return this.cachedCompanies$ ??= this.http.list({
      page: 'disabled',
      view: 'detail'
    }).pipe(
      map((response) => response.data),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Fetches all ProviderNetworkCompany records without the detail view.
   * @returns An observable that emits an array of all PNCs
   */
  getAllBasic$(): Observable<ProviderNetworkCompany[]> {
    return this.http.list({
      page: 'disabled'
    }).pipe(
      map((response) => response.data),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getInsuranceOptionsByState$(
    options: { state: string; forceReload?: boolean; includeCashPay?: boolean; }
  ): Observable<IInsurance[]> {
    const { state, forceReload = false, includeCashPay = false } = options;
    const cacheKey = `${state}:${includeCashPay}`;
    const cached = this.cachedInsuranceOptionsByState.get(cacheKey);

    if (cached && !forceReload) {
      return cached;
    }

    // Cache the in-flight observable, not just the resolved value, so concurrent SSR
    // subscribers for the same state share one request instead of a per-subscriber
    // cache stampede (the render otherwise fans out to this endpoint several times).
    const options$ = this.http.listInsuranceOptions$(
      { page: 'disabled', filter: { key: 'network_state', value: state } },
      includeCashPay
    ).pipe(
      map((resp) => resp.data.sort((a, b) => a.title.localeCompare(b.title))),
      catchError((err) => {
        this.cachedInsuranceOptionsByState.delete(cacheKey);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedInsuranceOptionsByState.set(cacheKey, options$);

    return options$;
  }

  /**
   * Fetches a lookup record for all ProviderNetworkCompany records.
   * @returns An observable that emits a lookup record for all PNCs
   */
  getLookup$(): Observable<Record<number, ProviderNetworkCompany>> {
    return this.getAll$().pipe(
      map((companies) => indexArray(companies, 'id'))
    );
  }

  reset(): void {
    delete this.cachedCompanies$;
  }
}
